import { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import JsonCodeEditor from "../JsonCodeEditor";
import WidgetDataSourcesEditor from "../WidgetDataSourcesEditor";
import { AppAutocomplete, AppTextField } from "../form";
import { adminDataAdaptorService, type DataAdaptor } from "../../services/adminDataAdaptorService";
import type {
  WidgetDataSource,
  WidgetDataSourceEntry,
  WidgetParameter,
  WidgetQueryLanguage,
  WidgetSourceType,
} from "../../types/widget";

const DEFAULT_DATE_PARAMETERS: WidgetParameter[] = [
  { name: "from", type: "datetime", bind: "dashboard.dateFrom", required: true },
  { name: "to", type: "datetime", bind: "dashboard.dateTo", required: true },
];

function ensureDateParameters(parameters?: WidgetParameter[]): WidgetParameter[] {
  const list = parameters?.length ? [...parameters] : [];
  const names = new Set(list.map((p) => p.name).filter(Boolean));
  for (const def of DEFAULT_DATE_PARAMETERS) {
    if (!names.has(def.name)) {
      list.push({ ...def });
    }
  }
  return list.length ? list : DEFAULT_DATE_PARAMETERS.map((p) => ({ ...p }));
}

type Props = {
  dataSource: WidgetDataSource | undefined;
  sources: WidgetDataSourceEntry[];
  onDataSourceChange: (patch: Partial<WidgetDataSource>) => void;
  onSourcesChange: (sources: WidgetDataSourceEntry[]) => void;
  dslText: string;
  onDslTextChange: (text: string) => void;
  dslJsonError?: boolean;
  queryBodyText: string;
  onQueryBodyTextChange: (text: string) => void;
  disabled?: boolean;
};

const DEFAULT_SQL = "SELECT * FROM my_table LIMIT 100";

export default function WidgetQuerySourcePanel({
  dataSource,
  sources,
  onDataSourceChange,
  onSourcesChange,
  dslText,
  onDslTextChange,
  dslJsonError,
  queryBodyText,
  onQueryBodyTextChange,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const sourceType: WidgetSourceType = dataSource?.sourceType ?? "OPENSEARCH";
  const queryLanguage: WidgetQueryLanguage =
    dataSource?.queryLanguage ?? (sourceType === "ADAPTOR" ? "SQL" : "OPENSEARCH_DSL");
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);
  const [adaptorsError, setAdaptorsError] = useState<string | null>(null);

  useEffect(() => {
    adminDataAdaptorService
      .list()
      .then((items) => {
        setAdaptors(items.filter((a) => a.enabled && a.id));
        setAdaptorsError(null);
      })
      .catch((e: unknown) => {
        setAdaptorsError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  const selectedAdaptor = useMemo(
    () => adaptors.find((a) => a.id === dataSource?.adaptorId),
    [adaptors, dataSource?.adaptorId]
  );

  const onSourceTypeChange = (next: WidgetSourceType) => {
    if (next === "ADAPTOR") {
      onDataSourceChange({
        sourceType: "ADAPTOR",
        queryLanguage: selectedAdaptor?.type === "MONGODB" ? "MONGO" : "SQL",
        queryBody: queryBodyText || DEFAULT_SQL,
        parameters: ensureDateParameters(dataSource?.parameters),
      });
      if (!queryBodyText.trim()) {
        onQueryBodyTextChange(
          selectedAdaptor?.type === "MONGODB"
            ? '{\n  "find": "my_collection",\n  "filter": {},\n  "limit": 100\n}'
            : DEFAULT_SQL
        );
      }
      return;
    }
    onDataSourceChange({
      sourceType: "OPENSEARCH",
      queryLanguage: "OPENSEARCH_DSL",
      adaptorId: undefined,
      queryBody: undefined,
    });
  };

  const onAdaptorPick = (adaptor: DataAdaptor | null) => {
    if (!adaptor?.id) {
      onDataSourceChange({ adaptorId: undefined });
      return;
    }
    const lang: WidgetQueryLanguage = adaptor.type === "MONGODB" ? "MONGO" : "SQL";
    onDataSourceChange({ adaptorId: adaptor.id, queryLanguage: lang });
  };

  return (
    <Stack spacing={2}>
      <FormControl disabled={disabled}>
        <Typography variant="subtitle2" gutterBottom>
          {t("widget.sourceType")}
        </Typography>
        <RadioGroup
          row
          value={sourceType}
          onChange={(e) => onSourceTypeChange(e.target.value as WidgetSourceType)}
        >
          <FormControlLabel value="OPENSEARCH" control={<Radio size="small" />} label={t("widget.sourceOpenSearch")} />
          <FormControlLabel value="ADAPTOR" control={<Radio size="small" />} label={t("widget.sourceAdaptor")} />
        </RadioGroup>
      </FormControl>

      {sourceType === "OPENSEARCH" ? (
        <>
          <WidgetDataSourcesEditor sources={sources} onChange={onSourcesChange} disabled={disabled} />
          <JsonCodeEditor
            label={t("widget.dslTemplate")}
            value={dslText}
            onChange={onDslTextChange}
            error={dslJsonError}
            helperText={dslJsonError ? t("widget.invalidJson") : undefined}
            height={360}
            readOnly={disabled}
          />
        </>
      ) : (
        <>
          <AppAutocomplete<DataAdaptor>
            label={t("widget.adaptor")}
            options={adaptors}
            value={selectedAdaptor ?? null}
            disabled={disabled}
            autoHighlight
            openOnFocus
            getOptionLabel={(a) => `${a.name} (${a.type})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, option) => onAdaptorPick(option)}
          />
          {adaptorsError ? (
            <Typography variant="caption" color="error">
              {adaptorsError}
            </Typography>
          ) : null}
          <AppTextField
            label={t("widget.queryLanguage")}
            value={queryLanguage}
            disabled
            size="small"
          />
          {queryLanguage === "MONGO" ? (
            <JsonCodeEditor
              label={t("widget.queryBodyMongo")}
              value={queryBodyText}
              onChange={onQueryBodyTextChange}
              height={360}
              readOnly={disabled}
            />
          ) : (
            <AppTextField
              label={t("widget.queryBodySql")}
              value={queryBodyText}
              onChange={(e) => onQueryBodyTextChange(e.target.value)}
              multiline
              minRows={12}
              disabled={disabled}
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />
          )}
        </>
      )}
    </Stack>
  );
}
